// src/pages/KPI/KPIProfiles.tsx — Редизайн 2026 (стиль Psychodiagnostics) + UX v2
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import {
  ArrowLeft, Calculator, Download, X, HelpCircle,
  Code, Megaphone, TrendingUp, Users, Wallet, Factory, Truck, Headphones,
  ChevronLeft, ChevronRight, BarChart3, Layers, Target, Info, CheckCircle2,
} from 'lucide-react';

// ───────── Типы ─────────
interface InputField {
  name: string;
  label: string;
  type: string;
  defaultValue?: number | string;
}
interface KPI {
  _id: string;
  name: string;
  description: string;
  formula: string;
  unit: string;
  inputFields?: InputField[];
}
interface SubCategory { name: string; kpis: KPI[]; }
interface Profile { _id: string; name: string; description: string; subCategories: SubCategory[]; }
interface CalcModalState { open: boolean; kpi: KPI | null; subCategoryName: string; }
interface Result { value: number; unit: string; }

// ───────── Визуальные стили профилей ─────────
const PROFILE_VISUALS = [
  { icon: Code,       gradient: 'linear-gradient(135deg, #6a11cb, #2575fc)' },
  { icon: Megaphone,  gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
  { icon: TrendingUp, gradient: 'linear-gradient(135deg, #10b981, #059669)' },
  { icon: Users,      gradient: 'linear-gradient(135deg, #8b5cf6, #6a11cb)' },
  { icon: Wallet,     gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' },
  { icon: Factory,    gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
  { icon: Truck,      gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)' },
  { icon: Headphones, gradient: 'linear-gradient(135deg, #ec4899, #db2777)' },
];
const getVisual = (idx: number) => PROFILE_VISUALS[idx % PROFILE_VISUALS.length];

// ───────── Приятные подсказки к часто встречающимся полям ─────────
const FIELD_HINTS: Record<string, string> = {
  totalTasks: 'Сколько всего задач было за период',
  totalDays: 'Суммарное время выполнения всех задач в днях',
  coveredLines: 'Сколько строк кода покрыто тестами',
  totalLines: 'Сколько всего строк кода в проекте',
  deployments: 'Сколько раз выкатывали релиз',
  days: 'За какой период (в днях)',
  incidents: 'Сколько было инцидентов / сбоев',
  totalRecoveryHours: 'Суммарное время восстановления (часы)',
  totalResolutionHours: 'Суммарное время решения (часы)',
  bugs: 'Сколько багов найдено',
  totalKLOC: 'Размер кода в тысячах строк (KLOC)',
  responseTime: 'Время отклика сервера (миллисекунды)',
  successful: 'Сколько успешных запросов',
  totalRequests: 'Сколько всего запросов',
  cpuUsage: 'Средняя загрузка процессора (%)',
  fcpValue: 'First Contentful Paint (мс)',
  lcpValue: 'Largest Contentful Paint (мс)',
  clsValue: 'Cumulative Layout Shift (баллы)',
  ttiValue: 'Time To Interactive (сек)',
  rerenders: 'Сколько раз компонент перерисовывался',
  actions: 'Сколько действий сделал пользователь',
  bundleSize: 'Размер JS-бандла (КБ)',
  fontLoadTime: 'Время загрузки шрифтов (мс)',
  clicks: 'Количество кликов по рекламе',
  impressions: 'Сколько раз показали рекламу',
  totalCost: 'Общие расходы (рубли)',
  acquisitions: 'Сколько клиентов привлекли',
  revenue: 'Выручка (рубли)',
  adCost: 'Расходы на рекламу (рубли)',
  conversions: 'Сколько конверсий произошло',
  visitors: 'Сколько было посетителей',
  unsubscribes: 'Сколько человек отписались',
  delivered: 'Сколько писем доставлено',
  leads: 'Количество лидов',
  avgCheck: 'Средний чек (рубли)',
  purchaseFreq: 'Сколько покупок в среднем на клиента',
  lifespan: 'Сколько лет клиент с вами',
  organicVisits: 'Визиты из поиска',
  totalPositions: 'Сумма позиций по всем ключам',
  keywords: 'Количество ключевых слов',
  drValue: 'Domain Rating (0-100)',
  refDomains: 'Количество ссылающихся доменов',
  singlePageVisits: 'Визиты с просмотром 1 страницы',
  totalVisits: 'Всего визитов',
  deals: 'Количество сделок',
  dealsCount: 'Количество сделок',
  totalRevenue: 'Общая выручка (рубли)',
  planRevenue: 'Плановая выручка (рубли)',
  actualRevenue: 'Фактическая выручка (рубли)',
  totalActivities: 'Общее число активностей по сделкам',
  repeatClients: 'Клиенты с повторными покупками',
  totalClients: 'Всего клиентов',
  totalChecks: 'Количество чеков',
  returns: 'Количество возвратов',
  sales: 'Количество продаж',
  orders: 'Количество оформленных заказов',
  carts: 'Количество корзин',
  totalItems: 'Общее количество товаров',
  closedVacancies: 'Сколько вакансий закрыли',
  passedProbation: 'Сколько прошли испытательный срок',
  totalHired: 'Всего нанято',
  candidatesOnStage: 'Кандидаты на текущем этапе',
  candidatesPrevStage: 'Кандидаты на предыдущем этапе',
  totalWaitHours: 'Суммарное время ожидания (часы)',
  totalResponses: 'Количество откликов',
  departed: 'Сколько уволилось',
  avgHeadcount: 'Среднесписочная численность',
  promotersPercent: 'Процент промоутеров (0-100)',
  detractorsPercent: 'Процент критиков (0-100)',
  totalEmployees: 'Количество сотрудников',
  totalYears: 'Суммарный стаж всех сотрудников (лет)',
  internalHires: 'Внутренние назначения',
  totalHires: 'Всего назначений',
  opex: 'Операционные расходы (рубли)',
  totalExpenses: 'Общие расходы (рубли)',
  netProfit: 'Чистая прибыль (рубли)',
  avgAssets: 'Средняя стоимость активов (рубли)',
  debt: 'Заёмный капитал (рубли)',
  equity: 'Собственный капитал (рубли)',
  variableCosts: 'Переменные затраты (рубли)',
  contributionMargin: 'Маржинальная прибыль (рубли)',
  currentAssets: 'Оборотные активы (рубли)',
  currentLiabilities: 'Краткосрочные обязательства (рубли)',
  cash: 'Денежные средства (рубли)',
  receivables: 'Дебиторская задолженность (рубли)',
  avgReceivables: 'Средняя дебиторская задолженность (рубли)',
  receivablesTurnover: 'Оборачиваемость дебиторки (раз)',
  cogs: 'Себестоимость продаж (рубли)',
  avgPayables: 'Средняя кредиторская задолженность (рубли)',
  actualCost: 'Фактические расходы (рубли)',
  budgetCost: 'Плановый бюджет (рубли)',
  plannedRevenue: 'Плановый план выручки (рубли)',
  incomeTax: 'Налог на прибыль (рубли)',
  pretaxProfit: 'Прибыль до налога (рубли)',
  totalTaxes: 'Всего уплачено налогов (рубли)',
  availability: 'Доступность оборудования (%)',
  performance: 'Производительность (%)',
  quality: 'Качество продукции (%)',
  defects: 'Количество дефектов',
  totalProduced: 'Всего произведено',
  availableMinutes: 'Доступное рабочее время (мин)',
  requiredOutput: 'Требуемый объём выпуска',
  totalOperationalHours: 'Суммарное время работы (часы)',
  failures: 'Количество отказов',
  repairs: 'Количество ремонтов',
  totalRepairHours: 'Суммарное время ремонта (часы)',
  complaints: 'Количество рекламаций',
  units: 'Количество единиц продукции',
  opportunities: 'Возможности для дефектов',
  usl: 'Верхняя граница допуска',
  lsl: 'Нижняя граница допуска',
  mean: 'Среднее значение',
  sigma: 'Стандартное отклонение (σ)',
  totalOutput: 'Общий объём выпуска',
  avgEmployees: 'Среднесписочная численность',
  productiveHours: 'Продуктивное время (часы)',
  availableHours: 'Доступное время (часы)',
  plannedRepairs: 'Плановые ремонты',
  totalRepairs: 'Всего ремонтов',
  totalMaintenanceCost: 'Затраты на ТО (рубли)',
  avgInventory: 'Средняя стоимость запасов (рубли)',
  deadStock: 'Неликвиды',
  totalStock: 'Общие запасы',
  matchedItems: 'Совпавшие позиции',
  totalPickingMinutes: 'Суммарное время комплектации (мин)',
  otifOrders: 'Заказы доставленные вовремя',
  totalOrders: 'Всего заказов',
  totalTransportCost: 'Транспортные расходы (рубли)',
  totalOrdersDelivered: 'Доставленные заказы',
  actualWeight: 'Фактический вес груза',
  maxCapacity: 'Максимальная грузоподъёмность',
  totalDistance: 'Суммарный пробег (км)',
  deliveries: 'Количество доставок',
  logisticsCost: 'Логистические затраты (рубли)',
  unitsProcessed: 'Обработано единиц товара',
  warehouseCost: 'Складские затраты (рубли)',
  totalDeliveries: 'Всего доставок',
  totalScore: 'Сумма оценок',
  fcrResolved: 'Решено с первого контакта',
  totalTickets: 'Количество обращений',
  totalFirstReplyMinutes: 'Суммарное время первого ответа (мин)',
  clientsStart: 'Клиенты на начало периода',
  clientsEnd: 'Клиенты на конец периода',
  repeatedTickets: 'Повторные обращения',
  totalReplySeconds: 'Суммарное время ответа (сек)',
  channelTickets: 'Обращений по каналу',
  lostClients: 'Ушедшие клиенты',
  agents: 'Количество агентов',
  totalCalls: 'Количество звонков/чатов',
  totalHoldMinutes: 'Суммарное время ожидания (мин)',
};

const getFieldHint = (field: InputField): string => {
  if (FIELD_HINTS[field.name]) return FIELD_HINTS[field.name];
  return field.label;
};

// ───────── Подсказка для target в зависимости от unit ─────────
const getTargetHint = (unit: string): string => {
  const u = (unit || '').toLowerCase();
  if (u === '%') return 'Значение в процентах. Например, для CTR — 5%, для Code Coverage — 80%.';
  if (u === '₽') return 'Сумма в рублях, которой хотите достичь.';
  if (u === 'дни' || u === 'дней') return 'Максимально допустимое количество дней.';
  if (u === 'часы' || u === 'мин') return 'Максимально допустимое время.';
  if (u === 'раз') return 'Желаемое значение (в разах).';
  if (u === 'мс') return 'Максимально допустимое время в миллисекундах.';
  if (u === 'сек') return 'Максимально допустимое время в секундах.';
  if (u === 'КБ') return 'Максимально допустимый размер в килобайтах.';
  return 'Значение, к которому вы стремитесь.';
};

// ───────── Основной компонент ─────────
const KPIProfiles: React.FC = () => {
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [selectedSub, setSelectedSub] = useState<string>('all');
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState<string>('');
  const [calcModal, setCalcModal] = useState<CalcModalState>({ open: false, kpi: null, subCategoryName: '' });
  const [inputValues, setInputValues] = useState<Record<string, string | number>>({});
  const [targetValue, setTargetValue] = useState<string>('');
  const [result, setResult] = useState<Result | null>(null);
  const [showFormula, setShowFormula] = useState<boolean>(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as any });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await api.get('/kpi/profiles');
      setProfiles(res.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Ошибка загрузки профилей');
    }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const handleProfileChange = async (id: string) => {
    if (!id) { setSelectedProfile(null); return; }
    setLoadingProfile(true);
    setError('');
    try {
      const res = await api.get(`/kpi/profiles/${id}`);
      setSelectedProfile(res.data);
      setSelectedSub('all');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      setError('Ошибка загрузки данных профиля');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleExport = () => {
    if (!selectedProfile) return;
    const rows = [['Название', 'Описание', 'Формула', 'Ед.изм.', 'Подраздел']];
    displayedKPIs.forEach((kpi: any) => {
      rows.push([kpi.name, kpi.description, kpi.formula, kpi.unit, kpi.subName]);
    });
    const csvContent = rows.map(r => r.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kpi_${selectedProfile.name}_${selectedSub}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const openCalculator = (kpi: KPI, subCategoryName: string) => {
    const defaults: Record<string, string | number> = {};
    kpi.inputFields?.forEach(f => { defaults[f.name] = f.defaultValue ?? ''; });
    setInputValues(defaults);
    setTargetValue('');
    setResult(null);
    setShowFormula(true);
    setCalcModal({ open: true, kpi, subCategoryName });
  };

  const handleCalculate = async () => {
    if (!calcModal.kpi || !selectedProfile) return;
    try {
      const res = await api.post('/kpi/calculate', {
        profileId: selectedProfile._id,
        subCategoryName: calcModal.subCategoryName,
        kpiName: calcModal.kpi.name,
        inputs: inputValues,
      });
      setResult({ value: res.data.result, unit: res.data.unit });
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || 'Ошибка расчёта');
    }
  };

  const displayedKPIs = useMemo(() => {
    if (!selectedProfile) return [];
    return selectedProfile.subCategories
      .filter((s: SubCategory) => selectedSub === 'all' || s.name === selectedSub)
      .flatMap((s: SubCategory) => s.kpis.map((k: KPI) => ({ ...k, subName: s.name })));
  }, [selectedProfile, selectedSub]);

  const totalKPIs = useMemo(
    () => profiles.reduce((acc, p) => acc + p.subCategories.reduce((a, s) => a + s.kpis.length, 0), 0),
    [profiles]
  );

  return (
    <div style={styles.container}>
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />
      <div style={styles.bgBlur3} />

      <button
        onClick={() => navigate('/dashboard')}
        style={styles.backButton}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)';
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.transform = 'translateX(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 30px rgba(102,126,234,0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
          e.currentTarget.style.color = '#2575fc';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
          e.currentTarget.style.transform = 'translateX(0)';
          e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.05)';
        }}
      >
        <ArrowLeft size={18} />
        <span>На главный дашборд</span>
      </button>

      <div style={styles.main}>
        {/* HERO */}
        <div style={styles.hero}>
          <div style={styles.heroIconWrap}>
            <div style={styles.heroIconBg} />
            <div style={styles.heroIconContent}><span style={styles.heroIconEmoji}>🎯</span></div>
          </div>
          <div>
            <h1 style={styles.title}>KPI Конструктор</h1>
            <p style={styles.subtitle}>
              Готовые формулы метрик по отраслям. Выберите профиль → откройте KPI → введите свои данные → получите результат.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <div style={styles.heroChip}><Layers size={13} color="#667eea" /><span>{profiles.length} профилей</span></div>
              <div style={styles.heroChip}><BarChart3 size={13} color="#667eea" /><span>{totalKPIs} KPI метрик</span></div>
            </div>
          </div>
        </div>

        {error && <p style={{ textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>{error}</p>}

        {/* СЕТКА ПРОФИЛЕЙ */}
        {!selectedProfile && (
          <div style={styles.grid}>
            {profiles.map((p: Profile, idx: number) => {
              const visual = getVisual(idx);
              const IconC = visual.icon;
              const kpiCount = p.subCategories.reduce((a, s) => a + s.kpis.length, 0);
              return (
                <div key={p._id} className="psy-testcard" style={styles.card} onClick={() => handleProfileChange(p._id)}>
                  <div style={styles.iconWrap}>
                    <div style={{ ...styles.iconBg, background: visual.gradient }}>
                      <IconC size={28} color="#fff" strokeWidth={2} />
                    </div>
                  </div>
                  <div style={styles.cardBody}>
                    <h3 style={styles.cardTitle}>{p.name}</h3>
                    <p style={styles.cardDesc}>{p.description}</p>
                    <div style={styles.meta}>
                      <div style={styles.metaChip}><Layers size={13} color="#667eea" /><span>{p.subCategories.length} подразд.</span></div>
                      <div style={styles.metaChip}><BarChart3 size={13} color="#667eea" /><span>{kpiCount} KPI</span></div>
                    </div>
                  </div>
                  <div className="psy-play-icon" style={styles.playIcon}><ChevronRight size={16} color="#667eea" /></div>
                </div>
              );
            })}
          </div>
        )}

        {/* ДЕТАЛИ ПРОФИЛЯ */}
        {selectedProfile && (
          <>
            <div style={styles.detailBar}>
              <button onClick={() => setSelectedProfile(null)} style={styles.backToProfilesBtn}>
                <ChevronLeft size={16} /><span>К списку профилей</span>
              </button>
              <div style={styles.detailTitle}>
                <strong>{selectedProfile.name}</strong>
                <span style={{ color: '#8b8fa8', marginLeft: 10 }}>{selectedProfile.description}</span>
              </div>
              <button onClick={handleExport} style={styles.exportButton}>
                <Download size={16} /><span>Экспорт CSV</span>
              </button>
            </div>

            <div style={styles.subChips}>
              <button onClick={() => setSelectedSub('all')} style={{ ...styles.subChip, ...(selectedSub === 'all' ? styles.subChipActive : {}) }}>
                Все ({selectedProfile.subCategories.reduce((a, s) => a + s.kpis.length, 0)})
              </button>
              {selectedProfile.subCategories.map((s: SubCategory) => (
                <button key={s.name} onClick={() => setSelectedSub(s.name)} style={{ ...styles.subChip, ...(selectedSub === s.name ? styles.subChipActive : {}) }}>
                  {s.name} ({s.kpis.length})
                </button>
              ))}
            </div>

            <div style={styles.grid}>
              {displayedKPIs.map((kpi: any) => (
                <div key={kpi._id} className="psy-testcard" style={styles.kpiCard}>
                  <div style={styles.kpiHeader}>
                    <h3 style={styles.kpiTitle}>{kpi.name}</h3>
                    <span style={styles.unitBadge}>{kpi.unit}</span>
                  </div>
                  <p style={styles.cardDesc}>{kpi.description}</p>

                  <div style={styles.formulaBox}>
                    <span style={styles.formulaLabel}>Формула</span>
                    <div style={styles.formulaText}>{kpi.formula}</div>
                  </div>

                  <div style={styles.meta}>
                    <div style={styles.metaChip}><Layers size={12} color="#667eea" /><span>{kpi.subName}</span></div>
                    <div style={styles.metaChip}><Calculator size={12} color="#667eea" /><span>{kpi.inputFields?.length || 0} поля</span></div>
                  </div>

                  <button
                    onClick={() => openCalculator(kpi, kpi.subName)}
                    style={styles.calcButton}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 26px rgba(102,126,234,0.4)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(102,126,234,0.25)'; }}
                  >
                    <Calculator size={16} /><span>Рассчитать KPI</span>
                  </button>
                </div>
              ))}
              {displayedKPIs.length === 0 && !loadingProfile && (
                <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#999' }}>Нет KPI для отображения</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* МОДАЛКА КАЛЬКУЛЯТОРА */}
      {calcModal.open && calcModal.kpi && (
        <div style={modalStyles.overlay} onClick={() => setCalcModal({ open: false, kpi: null, subCategoryName: '' })}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            {/* Кнопка закрытия */}
            <button
              onClick={() => setCalcModal({ open: false, kpi: null, subCategoryName: '' })}
              style={modalStyles.closeX}
              aria-label="Закрыть"
            >
              <X size={18} color="#8b8fa8" />
            </button>

            {/* Hero */}
            <div style={modalStyles.modalHero}>
              <div style={modalStyles.modalIcon}>
                <Calculator size={22} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={modalStyles.modalTitle}>{calcModal.kpi.name}</h2>
                <div style={modalStyles.unitInline}>Единица измерения результата: <b>{calcModal.kpi.unit}</b></div>
              </div>
            </div>

            {/* Что это */}
            <div style={modalStyles.infoBlock}>
              <div style={modalStyles.infoHeader}>
                <Info size={14} color="#667eea" />
                <span>Что это</span>
              </div>
              <div style={modalStyles.infoText}>{calcModal.kpi.description}</div>
            </div>

            {/* Формула */}
            <div style={modalStyles.formulaBlock}>
              <div style={modalStyles.infoHeader}>
                <Calculator size={14} color="#667eea" />
                <span>Формула расчёта</span>
              </div>
              <div style={modalStyles.formulaCode}>{calcModal.kpi.formula}</div>
            </div>

            {/* Поля ввода */}
            <div style={modalStyles.sectionTitle}>
              <span style={modalStyles.sectionTitleText}>📥 Введите данные</span>
            </div>
            {calcModal.kpi.inputFields?.map((field: InputField) => (
              <div key={field.name} style={modalStyles.field}>
                <label style={modalStyles.label}>{field.label}</label>
                <input
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={inputValues[field.name] ?? ''}
                  onChange={e => setInputValues({
                    ...inputValues,
                    [field.name]: e.target.type === 'number' ? +e.target.value : e.target.value,
                  })}
                  placeholder={field.defaultValue !== undefined ? `Например: ${field.defaultValue}` : 'Введите значение'}
                  style={modalStyles.input}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#667eea'; e.currentTarget.style.boxShadow = '0 0 0 4px rgba(102,126,234,0.12)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(102,126,234,0.2)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
                <div style={modalStyles.hint}>
                  <HelpCircle size={12} color="#8b8fa8" />
                  <span>{getFieldHint(field)}</span>
                </div>
              </div>
            ))}

            {/* Целевое значение */}
            <div style={modalStyles.sectionTitle}>
              <span style={modalStyles.sectionTitleText}>🎯 Сравнить с планом (не обязательно)</span>
            </div>
            <div style={modalStyles.targetBox}>
              <div style={modalStyles.targetExplain}>
                <b>Целевое значение</b> — это ваш план: какого результата вы хотите достичь.
                Если заполнить — увидите, на сколько процентов выполнен план.
                <div style={{ marginTop: 4, color: '#8b8fa8' }}>{getTargetHint(calcModal.kpi.unit)}</div>
              </div>
              <div style={modalStyles.targetInputRow}>
                <input
                  type="number"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  placeholder="Оставьте пустым, если не нужно"
                  style={modalStyles.input}
                />
                <span style={modalStyles.targetUnit}>{calcModal.kpi.unit}</span>
              </div>
            </div>

            {/* Кнопка Рассчитать */}
            <button
              onClick={handleCalculate}
              style={modalStyles.calcButton}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <Calculator size={16} /><span>Рассчитать</span>
            </button>

            {/* Результат */}
            {result && (() => {
              const tNum = parseFloat(targetValue);
              const hasTarget = !isNaN(tNum) && tNum > 0;
              const pct = hasTarget ? (result.value / tNum) * 100 : null;
              return (
                <div style={modalStyles.result}>
                  <div style={modalStyles.resultLabel}>Результат</div>
                  <div style={modalStyles.resultValue}>
                    {result.value.toFixed(2)} <span style={modalStyles.resultUnit}>{result.unit}</span>
                  </div>
                  {hasTarget && pct !== null && (
                    <div style={{
                      ...modalStyles.resultCompare,
                      background: pct >= 100 ? 'rgba(16,185,129,0.12)' : pct >= 70 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                      color: pct >= 100 ? '#059669' : pct >= 70 ? '#d97706' : '#dc2626',
                    }}>
                      {pct >= 100 && <CheckCircle2 size={16} />}
                      <span>План выполнен на <b>{pct.toFixed(0)}%</b> (цель: {tNum} {result.unit})</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <style>{`
        @keyframes psy-fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .psy-testcard { animation: psy-fadeUp 0.5s ease-out backwards; }
        .psy-testcard:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(31,38,135,0.12), 0 8px 16px rgba(31,38,135,0.06) !important;
          border-color: rgba(102,126,234,0.3) !important;
        }
        .psy-testcard:hover .psy-play-icon { transform: scale(1.1); background: rgba(102,126,234,0.2) !important; }
      `}</style>
    </div>
  );
};

// ═══════════════════ СТИЛИ ═══════════════════
const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh', padding: '76px 20px 40px',
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 50%, #f0e8ff 100%)',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    position: 'relative', overflowX: 'clip' as any,
  },
  bgBlur1: { position: 'absolute', top: '-10%', left: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(106,17,203,0.18) 0%, rgba(106,17,203,0) 70%)', borderRadius: '50%', zIndex: 0, pointerEvents: 'none' },
  bgBlur2: { position: 'absolute', bottom: '-10%', right: '-5%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(37,117,252,0.18) 0%, rgba(37,117,252,0) 70%)', borderRadius: '50%', zIndex: 0, pointerEvents: 'none' },
  bgBlur3: { position: 'absolute', top: '40%', left: '50%', width: 500, height: 500, transform: 'translate(-50%, -50%)', background: 'radial-gradient(circle, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0) 70%)', borderRadius: '50%', zIndex: 0, pointerEvents: 'none' },
  backButton: { position: 'absolute', top: 20, left: 20, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 40, padding: '9px 18px', fontSize: 14, fontWeight: 600, color: '#2575fc', cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,0,0,0.05)', transition: 'all 0.25s ease', zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: 8 },
  main: { maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 },
  hero: { display: 'flex', alignItems: 'center', gap: 20, marginBottom: 22, padding: '4px 4px 0' },
  heroIconWrap: { position: 'relative', width: 64, height: 64, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  heroIconBg: { position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6a11cb, #2575fc, #00c6ff)', borderRadius: 20, boxShadow: '0 14px 32px rgba(102,126,234,0.32)' },
  heroIconContent: { position: 'relative', display: 'flex', alignItems: 'center', zIndex: 1 },
  heroIconEmoji: { fontSize: 32, lineHeight: 1 },
  title: { fontSize: 26, fontWeight: 800, margin: '0 0 4px', background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: -0.4, lineHeight: 1.15 },
  subtitle: { fontSize: 14, color: '#5a5f7a', margin: 0, maxWidth: 720, lineHeight: 1.45 },
  heroChip: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: 'rgba(102,126,234,0.08)', border: '1px solid rgba(102,126,234,0.15)', borderRadius: 100, fontSize: 11, fontWeight: 700, color: '#667eea' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20, marginBottom: 28 },
  card: { position: 'relative', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 24, padding: 22, boxShadow: '0 12px 32px rgba(31,38,135,0.08)', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', display: 'flex', flexDirection: 'column', gap: 14, height: '100%', boxSizing: 'border-box' },
  iconWrap: { width: 56, height: 56, flexShrink: 0 },
  iconBg: { width: 56, height: 56, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 24px rgba(102,126,234,0.25)' },
  cardBody: { flex: 1, display: 'flex', flexDirection: 'column', gap: 10 },
  cardTitle: { fontSize: 16, fontWeight: 800, color: '#1a1a2e', margin: 0, lineHeight: 1.3 },
  cardDesc: { fontSize: 13, color: '#5a5f7a', lineHeight: 1.55, margin: 0, flex: 1 },
  meta: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 },
  metaChip: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: 'rgba(102,126,234,0.08)', border: '1px solid rgba(102,126,234,0.15)', borderRadius: 100, fontSize: 11, fontWeight: 700, color: '#667eea' },
  playIcon: { position: 'absolute', top: 18, right: 18, width: 32, height: 32, borderRadius: '50%', background: 'rgba(102,126,234,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.25s' },
  detailBar: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18, flexWrap: 'wrap', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 20, padding: '12px 16px', boxShadow: '0 8px 24px rgba(31,38,135,0.06)' },
  backToProfilesBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(102,126,234,0.2)', borderRadius: 12, padding: '8px 14px', fontSize: 13, fontWeight: 700, color: '#2575fc', cursor: 'pointer', transition: 'all 0.2s' },
  detailTitle: { flex: 1, fontSize: 14, color: '#1a1a2e', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 },
  exportButton: { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 12, padding: '8px 14px', fontSize: 13, fontWeight: 700, color: '#10b981', cursor: 'pointer' },
  subChips: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  subChip: { padding: '7px 14px', borderRadius: 100, background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(102,126,234,0.2)', fontSize: 12, fontWeight: 700, color: '#5a5f7a', cursor: 'pointer', transition: 'all 0.2s' },
  subChipActive: { background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', borderColor: 'transparent', boxShadow: '0 8px 20px rgba(102,126,234,0.3)' },
  kpiCard: { background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 22, padding: 20, boxShadow: '0 10px 28px rgba(31,38,135,0.07)', display: 'flex', flexDirection: 'column', gap: 10 },
  kpiHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
  kpiTitle: { fontSize: 15, fontWeight: 800, color: '#1a1a2e', margin: 0, lineHeight: 1.3, flex: 1 },
  unitBadge: { fontSize: 10, fontWeight: 800, color: '#667eea', padding: '4px 10px', borderRadius: 100, background: 'rgba(102,126,234,0.1)', border: '1px solid rgba(102,126,234,0.2)', whiteSpace: 'nowrap' },
  formulaBox: { background: 'rgba(102,126,234,0.06)', border: '1px solid rgba(102,126,234,0.12)', borderRadius: 12, padding: '8px 12px' },
  formulaLabel: { fontSize: 10, fontWeight: 800, color: '#8b8fa8', textTransform: 'uppercase' as any, letterSpacing: 0.5 },
  formulaText: { fontSize: 12, color: '#1a1a2e', marginTop: 3, lineHeight: 1.4 },
  calcButton: { marginTop: 8, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none', borderRadius: 12, padding: '10px 16px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontWeight: 700, fontSize: 13, boxShadow: '0 8px 20px rgba(102,126,234,0.25)', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)' },
};

// ═══════════════════ СТИЛИ МОДАЛКИ ═══════════════════
const modalStyles: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,15,30,0.5)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 },
  modal: { position: 'relative', background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(24px)', borderRadius: 24, padding: 28, width: 520, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 30px 60px rgba(31,38,135,0.25)', border: '1px solid rgba(255,255,255,0.8)' },
  closeX: { position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: 10, background: 'rgba(102,126,234,0.08)', border: '1px solid rgba(102,126,234,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' },

  modalHero: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18, paddingRight: 30 },
  modalIcon: { width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 24px rgba(102,126,234,0.3)', flexShrink: 0 },
  modalTitle: { fontSize: 18, fontWeight: 800, color: '#1a1a2e', margin: 0, lineHeight: 1.3 },
  unitInline: { fontSize: 12, color: '#8b8fa8', marginTop: 4 },

  infoBlock: { background: 'rgba(102,126,234,0.05)', border: '1px solid rgba(102,126,234,0.12)', borderRadius: 14, padding: '10px 14px', marginBottom: 10 },
  infoHeader: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: '#667eea', textTransform: 'uppercase' as any, letterSpacing: 0.4, marginBottom: 4 },
  infoText: { fontSize: 13, color: '#1a1a2e', lineHeight: 1.5 },

  formulaBlock: { background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 14, padding: '10px 14px', marginBottom: 18 },
  formulaCode: { fontSize: 13, color: '#1a1a2e', lineHeight: 1.5, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' },

  sectionTitle: { marginTop: 12, marginBottom: 10, paddingBottom: 6, borderBottom: '1px dashed rgba(102,126,234,0.2)' },
  sectionTitleText: { fontSize: 12, fontWeight: 800, color: '#1a1a2e', textTransform: 'uppercase' as any, letterSpacing: 0.5 },

  field: { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: 700, color: '#1a1a2e', display: 'block', marginBottom: 6 },
  input: { width: '100%', padding: '11px 14px', borderRadius: 12, border: '1px solid rgba(102,126,234,0.2)', background: 'rgba(255,255,255,0.9)', fontSize: 14, color: '#1a1a2e', outline: 'none', boxSizing: 'border-box' as any, transition: 'all 0.2s' },
  hint: { display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 11, color: '#8b8fa8' },

  targetBox: { background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 14, padding: '12px 14px', marginBottom: 18 },
  targetExplain: { fontSize: 12, color: '#5a5f7a', lineHeight: 1.5, marginBottom: 10 },
  targetInputRow: { display: 'flex', gap: 8, alignItems: 'center' },
  targetUnit: { fontSize: 13, fontWeight: 700, color: '#8b8fa8', minWidth: 30, textAlign: 'center' as any },

  calcButton: { width: '100%', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none', borderRadius: 14, padding: '14px 20px', cursor: 'pointer', fontWeight: 700, fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 8px 20px rgba(102,126,234,0.3)', transition: 'all 0.2s' },

  result: { marginTop: 20, padding: 20, background: 'linear-gradient(135deg, rgba(106,17,203,0.05), rgba(37,117,252,0.05))', borderRadius: 18, textAlign: 'center' as any, border: '1px solid rgba(102,126,234,0.15)' },
  resultLabel: { fontSize: 11, fontWeight: 800, color: '#8b8fa8', textTransform: 'uppercase' as any, letterSpacing: 0.5, marginBottom: 4 },
  resultValue: { fontSize: 32, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.1 },
  resultUnit: { fontSize: 15, color: '#8b8fa8', fontWeight: 600 },
  resultCompare: { marginTop: 14, padding: '10px 14px', borderRadius: 12, fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' },
};

export default KPIProfiles;
